export const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // Sunday - Saturday : 0 - 6
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start (if desired)
  return new Date(d.setDate(diff));
};

export const getEndOfWeek = (date) => {
  const d = new Date(getStartOfWeek(date));
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

export const getStartOfPreviousWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - 7); // Go back one week
  return getStartOfWeek(d);
};

export const getEndOfPreviousWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - 7); // Go back one week
  return getEndOfWeek(d);
};

export const getStartOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

export const getEndOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
};

export const getStartOfLastMonth = (date) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - 1);
  return getStartOfMonth(d);
};

export const getEndOfLastMonth = (date) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - 1);
  return getEndOfMonth(d);
};

export const getStartOfPreviousMonth = (date, monthsAgo = 1) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - monthsAgo);
  return getStartOfMonth(d);
};

export const getEndOfPreviousMonth = (date, monthsAgo = 1) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - monthsAgo);
  return getEndOfMonth(d);
};

export const getStartOfLast90Days = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - 89); // Today - 89 days = 90 days inclusive
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getEndOfLast90Days = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

export const getStartOfPrevious90Days = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - 180); // 90 days before the start of the last 90 days
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getEndOfPrevious90Days = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - 91); // Day before the start of the last 90 days
  d.setHours(23, 59, 59, 999);
  return d;
};

export const getStartOfYearToDate = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), 0, 1);
};

export const getEndOfYearToDate = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

export const getStartOfPreviousYearToDate = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear() - 1, 0, 1);
};

export const getEndOfPreviousYearToDate = (date) => {
  const d = new Date(date);
  return new Date(
    d.getFullYear() - 1,
    d.getMonth(),
    d.getDate(),
    23,
    59,
    59,
    999,
  );
};

export const getLongFormatDate = (date) => {
  if (!date) return null;

  const d = new Date(date);

  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "long" });
  const year = d.getFullYear();

  return `${day} ${month} ${year}`;
};

export const formatDateToShortMonth = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0"); // 01–12
  const day = String(d.getDate()).padStart(2, "0");

  return `${day}-${month}-${year}`;
};
export const formatDateToISOCustom = (date) => {
  if (!date) return "";
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0"); // 01–12
  const day = String(d.getDate()).padStart(2, "0"); // 01–31

  return `${year}-${month}-${day}`;
};

export const firstOfEveryMonth = (date) => {
  if (!date) return "";
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}-01`;
};

/**
 
 * @returns {string} IANA timezone (e.g. "Asia/Kolkata", "Asia/Manila")
 */
export const getUserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/**
 
 * @param {Date|string} date - Date to format
 * @param {string} timeZone - IANA timezone (e.g. "Asia/Kolkata")
 * @returns {string} YYYY-MM-DD
 */
export const formatISODate = (date, timeZone) => {
  if (!date) return "";
  const d = new Date(date);
  const parts = new Intl.DateTimeFormat("en", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);

  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${y}-${m}-${day}`;
};

/**
 * Formats a date as HH:MM:SS (24-hour) using Intl.DateTimeFormat (no hardcoded country locale).
 * @param {Date|string} date - Date to format
 * @param {string} timeZone - IANA timezone (e.g. "Asia/Kolkata")
 * @returns {string} HH:MM:SS
 */
export const formatTime = (date, timeZone) => {
  if (!date) return "";
  const d = new Date(date);
  const parts = new Intl.DateTimeFormat("en", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);

  const h = parts.find((p) => p.type === "hour")?.value;
  const m = parts.find((p) => p.type === "minute")?.value;
  const s = parts.find((p) => p.type === "second")?.value;

  return `${h}:${m}:${s}`;
};

/**
 * Formats a date for notifications: "Today", "Yesterday", or YYYY-MM-DD.
 * @param {Date|string} date - Date to format
 * @param {string} timeZone - IANA timezone (e.g. "Asia/Kolkata")
 * @returns {string}
 */
export const formatNotificationDate = (date, timeZone) => {
  if (!date) return "";
  const dateStr = formatISODate(date, timeZone);
  const todayStr = formatISODate(new Date(), timeZone);
  const yesterdayStr = formatISODate(new Date(Date.now() - 86400000), timeZone);

  if (dateStr === todayStr) return "Today";
  if (dateStr === yesterdayStr) return "Yesterday";
  return dateStr;
};

/** Returns { startDate, endDate } for the given sortBy value (e.g. SortByValue.WEEK). */
export const getDateRangeForSortBy = (sortByValue, today = new Date()) => {
  let startDate, endDate;
  switch (sortByValue) {
    case "currentMonth":
      startDate = getStartOfMonth(today);
      endDate = getEndOfMonth(today);
      break;
    case "lastMonth":
      startDate = getStartOfLastMonth(today);
      endDate = getEndOfLastMonth(today);
      break;
    case "last90Days":
      startDate = getStartOfLast90Days(today);
      endDate = getEndOfLast90Days(today);
      break;
    case "yearToDate":
      startDate = getStartOfYearToDate(today);
      endDate = getEndOfYearToDate(today);
      break;
    case "month":
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 29);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
    case "week":
    default:
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
  }
  return { startDate, endDate };
};

export const parseCustomDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if ((isNaN(d.getTime()) && typeof dateStr === "string" && dateStr.includes("-")) || (!isNaN(d?.getTime()) && typeof dateStr === "string" && dateStr?.split("-")[2].length === 4)) {
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return new Date(parts[2], parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
  }
  return d;
};
